import { useTranslation } from 'react-i18next';
import type { Profile } from '../../lib/auth.ts';
import { ChipGroup, NumberField, SaveButton, SelectField } from './fields.tsx';
import { FtpChart, type FtpPoint } from './FtpChart.tsx';
import { GOALS, LEVELS, toggle } from './profileOptions.ts';

export function CyclingTab({
  profile, setProfile, ftpHistory, error, saving, saveLabel, onSave,
}: Readonly<{
  profile: Profile;
  setProfile: (p: Profile) => void;
  ftpHistory: FtpPoint[];
  error: string;
  saving: boolean;
  saveLabel: string;
  onSave: () => void;
}>) {
  const { t } = useTranslation();
  const levelOptions = LEVELS.map((id) => ({ id, label: t(`profile.options.level.${id}`) }));
  const goalOptions = GOALS.map((o) => ({ id: o.id, label: t(`profile.options.goals.${o.id}`) }));
  return (
    <div className="flex flex-col gap-4">
      <NumberField
        label={t('profile.cycling.ftp')}
        value={profile.ftp}
        onChange={(v) => setProfile({ ...profile, ftp: v })}
        min={50}
        max={500}
      />
      <NumberField
        label={t('profile.cycling.weight')}
        value={profile.weight_kg}
        onChange={(v) => setProfile({ ...profile, weight_kg: v })}
        min={30}
        max={200}
      />
      <NumberField
        label={t('profile.cycling.height')}
        value={profile.height_cm}
        onChange={(v) => setProfile({ ...profile, height_cm: v })}
        min={100}
        max={250}
      />
      <NumberField
        label={t('profile.cycling.birthYear')}
        value={profile.birth_year}
        onChange={(v) => setProfile({ ...profile, birth_year: v })}
        min={1940}
        max={2010}
      />
      <NumberField
        label={t('profile.cycling.lthr')}
        value={profile.lthr ?? null}
        onChange={(v) => setProfile({ ...profile, lthr: v })}
        min={100}
        max={230}
      />
      <NumberField
        label={t('profile.cycling.maxHr')}
        value={profile.max_hr ?? null}
        onChange={(v) => setProfile({ ...profile, max_hr: v })}
        min={100}
        max={230}
      />
      <p className="text-xs text-(--color-text-secondary) -mt-2">
        {t('profile.cycling.hrHint')}
      </p>

      <SelectField
        id="profile-level"
        label={t('profile.cycling.level')}
        placeholder={t('profile.cycling.selectLevel')}
        value={profile.level}
        options={levelOptions}
        onChange={(v) => setProfile({ ...profile, level: v })}
      />

      <ChipGroup
        legend={t('profile.cycling.goals')}
        options={goalOptions}
        isSelected={(id) => profile.goals?.includes(id) ?? false}
        onToggle={(id) => setProfile({ ...profile, goals: toggle(profile.goals, id) })}
        selectedClass="bg-(--color-accent)/20 text-(--color-accent) border-(--color-accent)"
      />

      <p className="text-xs text-(--color-text-secondary)">
        {t('profile.cycling.prefillHint')}
      </p>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <SaveButton
        onClick={onSave}
        disabled={saving}
        label={saveLabel}
        colorClass="bg-green-400 shadow-(--color-accent)/30"
      />

      {ftpHistory.length > 1 && <FtpChart data={ftpHistory} />}
    </div>
  );
}
